import { Grid, Stack, Typography } from "@mui/material";

import { DisplayTextField } from "../../../../components/common";
import { FormButtonGroup } from "../../../../components/form";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { oids } from "../../../../configs/Settings";
import { useFhirData } from "../../../../utils/useFhirData";
import { useFormatter } from "../../../../utils/useFormatter";

interface IProps {
  cpfValue: string;
  organizationAfter: fhir5.Organization;
  loading: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

function toAddressString(address: any): string {
  if (!address) return "";
  const line = Array.isArray(address?.line)
    ? address.line.filter(Boolean).join(", ")
    : "";
  const city = address?.city ? String(address.city) : "";
  const state = address?.state ? String(address.state) : "";
  const postalCode = address?.postalCode ? String(address.postalCode) : "";
  const country = address?.country ? String(address.country) : "";

  return [line, city, state, postalCode, country].filter(Boolean).join(" - ");
}

function findTelecomValue(
  contacts: any,
  system: "phone" | "email",
): string {
  if (!Array.isArray(contacts)) return "";
  for (const c of contacts) {
    const telecom = Array.isArray(c?.telecom) ? c.telecom : [];
    const match = telecom.find((t: any) => t?.system === system);
    if (match?.value) return String(match.value);
  }
  return "";
}

function findFirstAddress(contacts: any): any {
  if (!Array.isArray(contacts)) return null;
  for (const c of contacts) {
    // Alguns fluxos usam `address` como objeto; outros como array (ex.: normalizeAltToFhirOrganization).
    if (Array.isArray(c?.address) && c.address[0]) return c.address[0];
    if (c?.address && typeof c.address === "object") return c.address;
  }
  return null;
}

export function ReviewChangeToCnpj({
  cpfValue,
  organizationAfter,
  loading,
  onBack,
  onConfirm,
}: IProps) {
  const { user } = useSessionContext();
  const { findIdentifier, findType } = useFhirData;

  const phone = findTelecomValue(organizationAfter?.contact, "phone");
  const email = findTelecomValue(organizationAfter?.contact, "email");
  const address = toAddressString(findFirstAddress(organizationAfter?.contact));

  return (
    <Stack spacing={3} width="100%" alignItems="center">
      <Typography
        variant="body1"
        color="neutral700.main"
        textAlign="center"
      >
        Revise as informações da sua organização de saúde
      </Typography>

      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid item xs={12} sm={6}>
          <DisplayTextField
            label="CNPJ"
            mask={useFormatter.formatCNPJ}
            value={findIdentifier(
              organizationAfter?.identifier,
              `urn:oid:${oids.cnpj}`,
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DisplayTextField
            label="CPF"
            mask={useFormatter.formatCPF}
            value={user?.username || cpfValue}
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayTextField
            label="Razão Social"
            value={organizationAfter?.name || ""}
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayTextField
            label="Natureza Jurídica"
            value={
              findType(organizationAfter?.type, "naturezaJuridica")?.display ||
              organizationAfter?.qualification?.[0]?.code?.coding?.find(
                (el: any) => el?.system?.includes("naturezaJuridica"),
              )?.display
            }
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayTextField
            label="CNAE Principal"
            value={`${
              organizationAfter?.qualification?.[0]?.code?.coding?.find(
                (el: any) =>
                  el?.use === "official" || el?.system?.includes("86"),
              )?.system || ""
            } - ${
              organizationAfter?.qualification?.[0]?.code?.coding?.find(
                (el: any) =>
                  el?.use === "official" || el?.system?.includes("86"),
              )?.display || ""
            }`}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DisplayTextField
            label="Telefone"
            value={phone}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DisplayTextField
            label="E-mail"
            value={email}
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayTextField
            label="Endereço"
            value={address}
          />
        </Grid>
      </Grid>

      <Stack width="100%" mt={1}>
        <FormButtonGroup
          onGoBackButtonClick={onBack}
          onNextButtonClick={onConfirm}
          goBackButtonText="Voltar"
          nextButtonText="Confirmar"
          loading={loading}
        />
      </Stack>
    </Stack>
  );
}

